import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useRecoilValue, useRecoilState, useSetRecoilState, useRecoilCallback, selector } from 'recoil';
import { useTranslation } from 'react-i18next';
import { atom_tourState, atom_isDesktop, atom_showSmartAgent, atom_originalTourState } from '@app/shared/state/tour.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { atom_isMapReady } from '@app/shared/state/map-ready.atom';
import { atom_showModalContent, atom_modalType, atom_smallStopImageWidth, atom_navigationHelperState } from '@app/shared/state/show-content-modal.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { atom_selectedUserPlace } from '@app/shared/state/user-added-stops.atom';
import { atom_savedStopState } from '@app/shared/state/saved-stop-state.atom';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { Stop } from '@app/shared/models/tour.type';
import { MapHeader } from '@app/new-app/MapHeader';
import { MapFilters } from '@app/new-app/MapFilters';
import { ListRender } from '@app/new-app/ListRender';
import { SideBar } from '@app/shared/components/sidebar-buttons/SideBar';
import SmartAgent from '@app/pages/smart-agent/SmartAgent';
import { AccessibleModal } from '@app/shared/components/accessibility/AccessibleModal';
import { announce } from '@app/shared/components/accessibility/LiveAnnouncer';
import { renderModalContent } from '@app/shared/components/modalComponents/ModalContentRenderer';
import NavigationHelperContent from '@app/shared/components/NavigationHelperContent';
import { InitialDataFetch } from '@app/features/initial-data-fetch/InitialDataFetch';
import { NotificationToast } from '@app/shared/components/NotificationToast';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { passiveCollector, isWithinRadius } from '@app/shared/services/passive-data/passive-data-collector';
import { useRenderTracker } from '@app/shared/debug';
import { safePushState, safeHistoryBack } from '@app/shared/utils/safe-history';

// ─── Selectors ───
const selector_tourConfig = selector({
	key: 'mapOverlays_tourConfig',
	get: ({ get }) => get(atom_tourState)?.config,
});

const selector_hasStops = selector({
	key: 'mapOverlays_hasStops',
	get: ({ get }) => (get(atom_tourState)?.stops?.length || 0) > 0,
});

const selector_stopsCount = selector({
	key: 'mapOverlays_stopsCount',
	get: ({ get }) => get(atom_tourState)?.stops?.length || 0,
});

// ─── SmartAgent Overlay ───
export const SmartAgentOverlay = React.memo(function SmartAgentOverlay() {
	useRenderTracker('SmartAgentOverlay');
	const [isShowSmartAgent, setIsShowSmartAgent] = useRecoilState(atom_showSmartAgent);
	const isDesktop = useRecoilValue(atom_isDesktop);
	const config = useRecoilValue(selector_tourConfig);
	const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
	const isDemo = uID?.toLowerCase() === 'demo';

	if (!isShowSmartAgent) return null;

	const onBack = () => { passiveCollector.trackChatScreenClosed(); setIsShowSmartAgent(false); };

	return isDesktop ? (
		<div style={{
			position: 'absolute', zIndex: 2000, bottom: 90, right: 90,
			width: '380px', maxWidth: '35vw', height: '60vh',
			boxShadow: '0px 6px 16px rgba(0,0,0,0.2)', borderRadius: 16,
			overflow: 'hidden', background: 'white',
			pointerEvents: isDemo ? 'none' : 'auto', opacity: isDemo ? 0.5 : 1,
		}}>
			<SmartAgent onBackToMapClicked={onBack} />
		</div>
	) : (
		<div style={{
			zIndex: 1000, overflow: 'auto',
			maxHeight: `calc(100vh - ${String(config?.sidebar) !== 'false' ? '60px' : '0px'})`,
			position: 'absolute', top: 0, left: 0, right: 0,
			bottom: String(config?.sidebar) !== 'false' ? 60 : 0,
			pointerEvents: isDemo ? 'none' : 'auto', opacity: isDemo ? 0.5 : 1,
		}}>
			<SmartAgent onBackToMapClicked={onBack} />
		</div>
	);
});

// ─── Mobile Header + SideBar ───
export const MobileHeaderOverlay = React.memo(function MobileHeaderOverlay() {
	useRenderTracker('MobileHeaderOverlay');
	const isDesktop = useRecoilValue(atom_isDesktop);
	const config = useRecoilValue(selector_tourConfig);
	const tourStage = useRecoilValue(atom_tourStage);
	const [isInfoClicked, setIsInfoClicked] = useState(false);

	if (isDesktop || !config) return null;

	return (
		<div style={{ zIndex: 999, pointerEvents: 'none' }}>
			<Box sx={{
				alignSelf: 'center', position: 'relative', zIndex: 0,
				display: 'flex', width: '100%', flexDirection: 'column', flex: 1,
				pointerEvents: 'auto',
			}}>
				<MapHeader
					setIsInfoClicked={setIsInfoClicked}
					showFilterNav={!config?.['omit_filters']}
					isDesktop={false}
					sidebarWidthPx={150}
				/>
				{tourStage === 'map' && String(config?.sidebar) !== 'false' && <SideBar />}
			</Box>
		</div>
	);
});

// ─── Mobile List ───
const noopToggle = () => { /* handled by SmartAgentOverlay via atom */ };

export const MobileListOverlay = React.memo(function MobileListOverlay() {
	useRenderTracker('MobileListOverlay');
	const isDesktop = useRecoilValue(atom_isDesktop);
	const isMapReady = useRecoilValue(atom_isMapReady);
	const hasStops = useRecoilValue(selector_hasStops);
	const tourStage = useRecoilValue(atom_tourStage);

	if (isDesktop || !isMapReady || !hasStops || tourStage !== 'map') return null;

	return <ListRender mode="mobile" onToggleSmartAgent={noopToggle} />;
});

// ─── Desktop Sidebar Panel (sidebar icons + header + list) ───
export const DesktopSidebarPanel = React.memo(function DesktopSidebarPanel() {
	useRenderTracker('DesktopSidebarPanel');
	const tourStage = useRecoilValue(atom_tourStage);
	const hasStops = useRecoilValue(selector_hasStops);
	const config = useRecoilValue(selector_tourConfig);
	const { i18n } = useTranslation();
	const isHebrew = () => i18n.language === 'he';

	const [sidebarWidthPx, setSidebarWidthPx] = useState(0);
	useEffect(() => {
		const handleResize = () => {
			const width = typeof window !== 'undefined' ? window.innerWidth : 0;
			setSidebarWidthPx(Math.max(Math.floor(width >= 1400 ? width * 0.25 : width >= 1100 ? width * 0.33 : width >= 1000 ? width * 0.4 : (width - 85) / 2), 400));
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const [isInfoClicked, setIsInfoClicked] = useState(false);

	if (tourStage !== 'map' || !hasStops) return null;

	const onToggleSmartAgent = () => { /* handled by SmartAgentOverlay via atom */ };

	return (
		<Box
			data-desktop-sidebar
			sx={{
				position: 'fixed', top: 0, left: 0,
				width: 'auto', height: '100vh', zIndex: 2000,
				display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
				borderRadius: 3, overflow: 'hidden',
				boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.15)', bgcolor: 'white', direction: 'ltr',
			}}
		>
			<Box sx={{ height: '100%' }}>
				{String(config?.sidebar) !== 'false' && <SideBar />}
			</Box>
			<Box sx={{
				position: 'relative', width: `${sidebarWidthPx || 0}px`, height: '100vh',
				bgcolor: 'white', boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.15)',
				display: 'flex', flexDirection: 'column', direction: isHebrew() ? 'rtl' : 'ltr',
			}}>
				<MapHeader
					setIsInfoClicked={setIsInfoClicked}
					showFilterNav={!config?.['omit_filters']}
					isDesktop={true}
					sidebarWidthPx={sidebarWidthPx}
				/>
				<Box sx={{ bgcolor: 'rgba(0,0,0,0.08)', m: 0 }} />
				<Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
					<ListRender mode="desktop" onToggleSmartAgent={onToggleSmartAgent} />
				</Box>
			</Box>
		</Box>
	);
});

// ─── Desktop Floating Filters ───
export const DesktopFloatingFilters = React.memo(function DesktopFloatingFilters() {
	useRenderTracker('DesktopFloatingFilters');
	const tourStage = useRecoilValue(atom_tourStage);
	const config = useRecoilValue(selector_tourConfig);

	// Read openedStopState on demand — only used for gating
	const getOpenedStop = useRecoilCallback(({ snapshot }) => () =>
		snapshot.getLoadable(atom_openedStopState).getValue()
	, []);

	const [sidebarWidthPx, setSidebarWidthPx] = useState(0);
	useEffect(() => {
		const handleResize = () => {
			const width = typeof window !== 'undefined' ? window.innerWidth : 0;
			setSidebarWidthPx(Math.max(Math.floor(width >= 1400 ? width * 0.25 : width >= 1100 ? width * 0.33 : width >= 1000 ? width * 0.4 : (width - 85) / 2), 400));
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Subscribe to opened stop for conditional render
	const openedStopState = useRecoilValue(atom_openedStopState);

	if (tourStage !== 'map' || config?.['omit_filters'] || openedStopState) return null;

	return (
		<Box sx={{ position: 'fixed', top: 0, left: sidebarWidthPx || 0, right: 0, zIndex: 1500, m: 0 }}>
			<MapFilters showAppBar={false} sidebarWidth={sidebarWidthPx} />
		</Box>
	);
});

// ─── Desktop Stops Count Badge ───
export const DesktopStopsCount = React.memo(function DesktopStopsCount() {
	useRenderTracker('DesktopStopsCount');
	const stopsCount = useRecoilValue(selector_stopsCount);
	const { t } = useTranslation();

	const [sidebarWidthPx, setSidebarWidthPx] = useState(0);
	useEffect(() => {
		const handleResize = () => {
			const width = typeof window !== 'undefined' ? window.innerWidth : 0;
			setSidebarWidthPx(Math.max(Math.floor(width >= 1400 ? width * 0.25 : width >= 1100 ? width * 0.33 : width >= 1000 ? width * 0.4 : (width - 85) / 2), 400));
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	if (stopsCount === 0) return null;

	return (
		<Box sx={{
			// Viewport-relative so x aligns with MapFilters' first button (same offset math)
			position: 'fixed',
			left: (sidebarWidthPx || 0) + 100,
			bottom: 16, zIndex: 1100, bgcolor: 'white', borderRadius: '20px',
			px: 2, py: 0.75,
			boxShadow: '0px 0px 6px 0px rgba(0,37,74,0.10)',
			border: '1px solid', borderColor: 'rgb(236, 236, 236)',
		}}>
			<Typography variant="body2">
				{stopsCount.toString()} {t('Stops')}
			</Typography>
		</Box>
	);
});

// ─── Modal Overlay ───
export const ModalOverlay = React.memo(function ModalOverlay() {
	useRenderTracker('ModalOverlay');
	const [state_showModal, setState_showModal] = useRecoilState(atom_showModalContent);
	const [state_modalType, setState_modalType] = useRecoilState(atom_modalType);
	const state_activeStopState = useRecoilValue(atom_activeStopState);
	const smallStopImageWidth = useRecoilValue(atom_smallStopImageWidth);
	const isDesktop = useRecoilValue(atom_isDesktop);
	const config = useRecoilValue(selector_tourConfig);
	const { t, i18n } = useTranslation();
	const isHebrew = () => i18n.language === 'he';

	const setNavState = useSetRecoilState(atom_navigationHelperState);
	const setListMode = useSetRecoilState(atom_listModeState);
	const setDropPinMode = useSetRecoilState(atom_dropPinMode);
	const setSelectedUserPlace = useSetRecoilState(atom_selectedUserPlace);
	const setSavedStopState = useSetRecoilState(atom_savedStopState);
	const { activeCTA, markAsSeen, dismissCTA, fireTrigger } = useNotificationTrigger();

	const [isInfoClicked, setIsInfoClicked] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [dropPinName, setDropPinName] = useState('');
	const modalTriggerRef = useRef<HTMLButtonElement>(null);

	// On-demand reads
	const getOnDemand = useRecoilCallback(({ snapshot }) => () => ({
		openedStopState: snapshot.getLoadable(atom_openedStopState).getValue(),
		geolocation: snapshot.getLoadable(atom_geolocationState).getValue(),
		savedStopState: snapshot.getLoadable(atom_savedStopState).getValue(),
	}), []);

	const openedStopStateRef = useRef<any>(null);
	// Keep ref in sync
	const openedStopState = useRecoilValue(atom_openedStopState);
	openedStopStateRef.current = openedStopState;

	// Back button history management
	const modalClosedByBackRef = useRef(false);
	const modalWasOpenRef = useRef(false);
	useEffect(() => {
		const isOpen = !!state_modalType;

		if (!isOpen) {
			if (modalWasOpenRef.current && !modalClosedByBackRef.current) {
				safeHistoryBack();
			}
			modalWasOpenRef.current = false;
			modalClosedByBackRef.current = false;
			return;
		}

		if (!modalWasOpenRef.current) {
			safePushState({ modal: true });
			modalClosedByBackRef.current = false;
			modalWasOpenRef.current = true;
		}

		const onPopState = (e: PopStateEvent) => {
			if (e.state?.modal) return;
			modalClosedByBackRef.current = true;

			if (state_modalType === 'smallStop' && state_activeStopState?.id) {
				const geo = getOnDemand().geolocation;
				passiveCollector.trackStopClosed(state_activeStopState.id, isWithinRadius(geo.latitude, geo.longitude, state_activeStopState as Stop));
			}

			const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith('stop_modal_state_'));
			keysToRemove.forEach(key => localStorage.removeItem(key));

			const wasDropAPin = state_modalType === 'dropAPin';
			setState_showModal(false);
			setState_modalType(null);

			if (wasDropAPin) {
				setDropPinMode({ isActive: false, step: null, pinLocation: null });
				setSelectedUserPlace(null);
			}

			if (getOnDemand().savedStopState) {
				setSavedStopState(null);
			}
		};

		window.addEventListener('popstate', onPopState);
		return () => window.removeEventListener('popstate', onPopState);
	}, [state_modalType]);

	// Close button press animation
	useEffect(() => {
		if (!state_showModal) return;
		const timer = setTimeout(() => {
			const closeBtn = document.querySelector('.react-responsive-modal-closeButton') as HTMLElement;
			if (!closeBtn) return;
			const onDown = () => { closeBtn.style.transform = 'scale(0.7)'; };
			const onUp = () => { closeBtn.style.transform = 'scale(1)'; };
			closeBtn.addEventListener('pointerdown', onDown);
			closeBtn.addEventListener('pointerup', onUp);
			closeBtn.addEventListener('pointerleave', onUp);
			(closeBtn as any)._cleanupAnim = () => {
				closeBtn.removeEventListener('pointerdown', onDown);
				closeBtn.removeEventListener('pointerup', onUp);
				closeBtn.removeEventListener('pointerleave', onUp);
			};
		}, 0);
		return () => {
			clearTimeout(timer);
			const closeBtn = document.querySelector('.react-responsive-modal-closeButton') as any;
			closeBtn?._cleanupAnim?.();
		};
	}, [state_showModal]);

	const handleClose = () => {
		if (openedStopStateRef.current) return;
		if (!state_showModal) return;

		if (state_modalType === 'desktopCTA' && activeCTA) {
			markAsSeen(activeCTA.id, 'seen');
			dismissCTA();
			return;
		}

		if (state_modalType === 'smallStop' && state_activeStopState?.id) {
			const geo = getOnDemand().geolocation;
			passiveCollector.trackStopClosed(state_activeStopState.id, isWithinRadius(geo.latitude, geo.longitude, state_activeStopState as Stop));
		}

		if (state_activeStopState?.type?.toLowerCase() === 'smallstop' && state_activeStopState?.id) {
			fireTrigger('action', { stopId: state_activeStopState.id });
			fireTrigger('locationAndAction', { stopId: state_activeStopState.id });
		}

		const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith('stop_modal_state_'));
		keysToRemove.forEach(key => localStorage.removeItem(key));

		const wasDropAPin = state_modalType === 'dropAPin';
		setState_showModal(false);
		setState_modalType(null);

		if (wasDropAPin) {
			setDropPinMode({ isActive: false, step: null, pinLocation: null });
			setDropPinName('');
			setSelectedUserPlace(null);
		}

		if (getOnDemand().savedStopState) {
			setSavedStopState(null);
		}
	};

	// Calculate modal width for small stops
	const smallStopMaxWidth = smallStopImageWidth
		? Math.max(Math.min(smallStopImageWidth + 20, 600), 320)
		: undefined;

	return (
		<div style={{ zIndex: 999, pointerEvents: 'none' }}>
			<AccessibleModal
				center={String(config?.sidebar) === 'false'}
				styles={{
					overlay: {
						background: 'rgba(0, 0, 0, 0.5)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						overflow: 'hidden',
						zIndex: 9999999,
					},
					modal: {
						borderRadius: 12,
						minWidth: isDesktop ? '400px' : 'auto',
						width: isDesktop
							? (state_activeStopState?.type?.toLowerCase() === 'smallstop' && smallStopImageWidth
								? `${Math.min(Math.max(smallStopImageWidth, 400), 550)}px`
								: 'auto')
							: '100%',
						maxWidth: isDesktop ? '550px' : 'calc(100vw - 20px)',
						maxHeight: isDesktop ? '90vh' : 'calc(100vh - 100px)',
						padding: 0,
						zIndex: 99999,
						background: '#fff',
						boxShadow: 'none',
						position: 'relative' as const,
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column' as const,
						transition: 'width 0.2s ease',
						...(state_modalType === 'stopsCards' && {
							border: `1px solid ${config?.mainColor || 'rgb(128, 134, 139)'}`,
						}),
						...(!isDesktop && String(config?.sidebar) !== 'false' && {
							marginTop: '-60px',
						}),
					},
					closeButton: {
						top: 15,
						...(isHebrew() ? { left: 20, right: 'auto' } : { right: 20 }),
						width: 36,
						height: 36,
						borderRadius: 18,
						background: state_modalType === 'linksModal' ? 'transparent' : '#F6F6F9',
						boxShadow: state_modalType === 'linksModal' ? 'none' : '0 2px 6px rgba(0,0,0,0.16)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						transition: 'transform 0.2s ease',
						transformOrigin: 'center',
						transform: isClosing ? 'scale(0.8)' : 'scale(1)',
						cursor: 'pointer',
					},
					closeIcon: {
						width: 20,
						height: 20,
						fill: '#333',
					},
					modalContainer: {
						zIndex: 9999999,
						position: 'fixed' as const,
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					},
				}}
				open={state_showModal && !openedStopState}
				onClose={handleClose}
				ariaLabelledBy={isInfoClicked ? "info-modal-title" : "stop-modal-title"}
				returnFocusRef={modalTriggerRef}
			>
				{/* Navigation button for stopsCards modal */}
				{(state_activeStopState?.type?.toLowerCase() === 'smallstop' || state_modalType === 'stopsCards') && state_modalType !== 'accessibility' && state_modalType !== 'linksModal' && (
					<Button
						onPointerDown={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.7)'; }}
						onPointerUp={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
						onPointerLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
						onClick={(e) => {
							if (isDesktop) {
								setNavState({
									anchorEl: e.currentTarget,
									offset: { top: 80, x: -30 },
									usePopover: true,
								});
							} else {
								setListMode('mobileNavigation');
							}
						}}
						aria-label={t('on_tour.stop.open_navigation') as string}
						sx={{
							border: '0.2px solid #F6F6F9',
							position: 'fixed', top: 15,
							right: isHebrew() ? 'auto' : 70,
							left: isHebrew() ? 70 : 'auto',
							width: '37px', height: '37px', minWidth: '36px',
							padding: 0, zIndex: 10,
							bgcolor: config?.mainColor || '#E14A1B',
							color: 'white', borderRadius: '50%',
							boxShadow: '0 2px 6px rgba(0,0,0,0.16)',
							transition: 'transform 0.15s ease',
						}}
					>
						<i className="fa-solid fa-diamond-turn-right" aria-hidden="true" style={{ fontSize: '20px' }} />
					</Button>
				)}
				<Box sx={{
					flex: 1, maxHeight: '80vh', minHeight: 0,
					overflowY: 'auto', WebkitOverflowScrolling: 'touch',
					pr: 0, scrollbarGutter: 'stable', position: 'relative', direction: 'ltr',
					scrollbarWidth: 'thin', scrollbarColor: '#9e9e9e #F6F6F6',
					'&::-webkit-scrollbar': { width: 10 },
					'&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
					'&::-webkit-scrollbar-track': { backgroundColor: '#F6F6F6' },
					'&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: 8, border: '2px solid transparent', backgroundClip: 'content-box' },
					'&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9e9e9e' },
				}}>
					<Box sx={{ direction: i18n.language === 'he' ? 'rtl' : 'ltr' }}>
						{renderModalContent()}
					</Box>
				</Box>
			</AccessibleModal>
		</div>
	);
});
